import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { initAction } from 'containers/AffiliationsFormPage/actions';
import epics from 'containers/AffiliationsFormPage/epics';
import reducer from 'containers/AffiliationsFormPage/reducers';
import { selectUser } from 'entities/user/selectors';
import { useAffiliationByIdLazyQuery } from 'generated/graphql';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';

import {
  getAffiliationType,
  getAffiliationBoardPosition,
} from '../AffiliationsList/getAffiliationType';

export const withInitialValues =
  (
    Component: React.ComponentType<{
      affiliationId: string;
      userId: string;
      loading: boolean;
      initialLocation: string;
      initialValues: Record<string, unknown>;
    }>
  ) =>
  (props): JSX.Element => {
    useInjectReducer({ key: 'affiliationsFormPage', reducer });
    useInjectEpics({ key: 'affiliationsFormPage', epics });

    const dispatch = useDispatch();

    const { userId, affiliationId } = useParams<{
      userId: string;
      affiliationId: string;
    }>();

    const [getAffiliationById, { data, loading }] = useAffiliationByIdLazyQuery();

    const me = useSelector(selectUser);

    useEffect(() => {
      if (me) {
        dispatch(initAction(userId));
      }
    }, [me, userId, dispatch]);

    useEffect(() => {
      if (affiliationId) {
        getAffiliationById({
          variables: {
            affiliationId,
          },
        });
      }
    }, [affiliationId, getAffiliationById]);

    return (
      <Component
        {...props}
        affiliationId={affiliationId}
        userId={userId}
        loading={loading}
        initialLocation={me?.organization?.locationObject?.countryCode}
        initialValues={{
          affiliationType: getAffiliationType(data?.affiliation?.affiliationType),
          boardPosition: getAffiliationBoardPosition(data?.affiliation?.boardPosition),
          ongoing: data?.affiliation?.endingAt ? null : true,
          nonprofits: data?.affiliation?.Nonprofit && [data?.affiliation?.Nonprofit],
          startingAt: data?.affiliation?.startingAt,
          endingAt: data?.affiliation?.endingAt,
        }}
      />
    );
  };

export default withInitialValues;
